import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useSocket } from '../SocketProvider';
import type { RealtimeNotificationPayload } from './types';
import type { InboxNotification } from 'app/modules/RealtimeNotificationsProvider/api';
import { useNotificationsInbox } from './useNotificationsInbox';

type RealtimeNotificationListener = (
  notification: RealtimeNotificationPayload,
) => void;

type RealtimeNotificationsContextValue = {
  subscribe: (listener: RealtimeNotificationListener) => () => void;
  unreadCount: number;
  unreadCountByPath: Record<string, number>;
  notifications: InboxNotification[];
  hasInboxData: boolean;
  isInboxLoading: boolean;
  isInboxFetching: boolean;
  refreshInbox: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
};

const RealtimeNotificationsContext = createContext<
  RealtimeNotificationsContextValue | undefined
>(undefined);

export function useRealtimeNotificationListener(
  handler: RealtimeNotificationListener,
) {
  const ctx = useContext(RealtimeNotificationsContext);

  if (!ctx) {
    throw new Error(
      'useRealtimeNotificationListener must be used within RealtimeNotificationsProvider',
    );
  }

  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const stableListener = useCallback(
    (notification: RealtimeNotificationPayload) => {
      handlerRef.current?.(notification);
    },
    [],
  );

  useEffect(() => {
    const unsubscribe = ctx.subscribe(stableListener);
    return unsubscribe;
  }, [ctx, stableListener]);
}

export function useRealtimeNotificationsInbox() {
  const ctx = useContext(RealtimeNotificationsContext);

  if (!ctx) {
    throw new Error(
      'useRealtimeNotificationsInbox must be used within RealtimeNotificationsProvider',
    );
  }

  return {
    unreadCount: ctx.unreadCount,
    unreadCountByPath: ctx.unreadCountByPath,
    notifications: ctx.notifications,
    hasInboxData: ctx.hasInboxData,
    isLoading: ctx.isInboxLoading,
    isFetching: ctx.isInboxFetching,
    refreshInbox: ctx.refreshInbox,
    markAsRead: ctx.markAsRead,
  };
}

export function RealtimeNotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { socket } = useSocket();
  const userId = useSelector(
    (state: { auth?: { user?: { id?: number | string | null } } }) =>
      state.auth?.user?.id ?? null,
  );
  const {
    notifications,
    unreadCount,
    unreadCountByPath,
    hasData,
    isLoading,
    isFetching,
    refetch,
    markAsRead,
    invalidateInbox,
  } = useNotificationsInbox();

  const listenersRef = useRef<Set<RealtimeNotificationListener>>(new Set());
  const seenNotificationKeysRef = useRef<Set<string>>(new Set());

  const mergeSeenKeysFromServer = useCallback((items: InboxNotification[]) => {
    items.forEach((notification) => {
      seenNotificationKeysRef.current.add(`id:${notification.id}`);
      if (notification.external_event_id) {
        seenNotificationKeysRef.current.add(
          `external:${notification.external_event_id}`,
        );
      }
    });
  }, []);

  useEffect(() => {
    seenNotificationKeysRef.current = new Set();
  }, [userId]);

  useEffect(() => {
    mergeSeenKeysFromServer(notifications);
  }, [notifications, mergeSeenKeysFromServer]);

  const refreshInbox = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    if (!socket) return;

    const handleSocketNotification = (
      notification: RealtimeNotificationPayload,
    ) => {
      listenersRef.current.forEach((listener) => listener(notification));

      const idKey = notification.id ? `id:${notification.id}` : undefined;
      const externalEventId =
        typeof notification.externalEventId === 'string'
          ? notification.externalEventId
          : undefined;
      const externalKey = externalEventId
        ? `external:${externalEventId}`
        : undefined;

      if (idKey && seenNotificationKeysRef.current.has(idKey)) {
        return;
      }
      if (externalKey && seenNotificationKeysRef.current.has(externalKey)) {
        return;
      }

      if (idKey) {
        seenNotificationKeysRef.current.add(idKey);
      }
      if (externalKey) {
        seenNotificationKeysRef.current.add(externalKey);
      }

      invalidateInbox();
    };

    socket.on('notification', handleSocketNotification);

    return () => {
      socket.off('notification', handleSocketNotification);
    };
  }, [socket, invalidateInbox]);

  const subscribe = useCallback((listener: RealtimeNotificationListener) => {
    listenersRef.current.add(listener);

    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const value = useMemo(
    () => ({
      subscribe,
      unreadCount,
      unreadCountByPath,
      notifications,
      hasInboxData: hasData,
      isInboxLoading: isLoading,
      isInboxFetching: isFetching,
      refreshInbox,
      markAsRead,
    }),
    [
      subscribe,
      unreadCount,
      unreadCountByPath,
      notifications,
      hasData,
      isLoading,
      isFetching,
      refreshInbox,
      markAsRead,
    ],
  );

  return (
    <RealtimeNotificationsContext.Provider value={value}>
      {children}
    </RealtimeNotificationsContext.Provider>
  );
}
